/**
 * Reciprocal Rank Fusion, for the browser.
 *
 *   score(d) = Σ  wᵢ / (k + rankᵢ(d))
 *
 * A port of api/_radar/rrf.js, which the radar uses to fuse four rankers. Same
 * reasoning applies here: a BM25 score and a cosine similarity are on
 * incomparable scales, and normalising them means inventing a conversion nobody
 * can defend. Ranks sidestep it.
 *
 * Pure — no I/O, no clock.
 */

const DEFAULT_K = 60;

export interface Ranking {
    key: string;
    weight?: number;
    /** Ids, best first. */
    order: string[];
}

export interface Fused {
    id: string;
    score: number;
    /** Each ranker's 1-indexed position, so a result can explain itself. */
    signals: Record<string, number>;
}

export function fuse(rankings: Ranking[], ids: string[], k = DEFAULT_K): Fused[] {
    const positions = new Map<string, Record<string, number>>(ids.map(id => [id, {}]));

    for (const ranking of rankings) {
        ranking.order.forEach((id, i) => {
            const entry = positions.get(id);
            if (entry) entry[ranking.key] = i + 1;
        });
    }

    const weights = new Map(rankings.map(r => [r.key, r.weight ?? 1]));

    return ids
        .map(id => {
            const signals = positions.get(id) ?? {};
            let score = 0;
            for (const [key, rank] of Object.entries(signals)) {
                score += (weights.get(key) ?? 1) / (k + rank);
            }
            return { id, score, signals };
        })
        .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
}
